/**
 * Decision Log Page
 *
 * Complete audit trail of every bot decision: scan results, pattern
 * evaluations (with per-criterion actual vs expected), signal generation,
 * risk-gate rejections, order executions, and exits -- filterable by bot
 * and asset class.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  RefreshCw, Filter, Radio, ChevronDown, ChevronUp, Rss,
  CheckCircle2, HelpCircle, Cpu, LogOut, AlertOctagon,
  BrainCircuit, Maximize, XCircle, PlayCircle, Activity,
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import { decisionsApi, diagnosticsApi, ApiError } from '../lib/api';
import { useTitanStore } from '../stores/titanStore';
import { useEquitiesMode } from '../hooks/useEquitiesMode';
import { filterStockPatterns } from '../lib/stocksOnly';
import { ClickableTicker } from '../components/TradingViewModal';
import DataAvailabilityPanel from '../components/titan/DataAvailabilityPanel';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';

// ============================================================
// Types
// ============================================================

interface CriterionResult {
  field: string;
  operator: string;
  expected_value: number | null;
  expected_value2: number | null;
  actual_value: number | null;
  passed: boolean;
  is_missing: boolean;
}

interface DetailedLog {
  id: string;
  timestamp: string;
  event_type: string;
  symbol?: string;
  pattern_id?: string;
  pattern_name?: string;
  // pattern_eval
  matched?: boolean;
  criteria_passed?: number;
  criteria_total?: number;
  criteria_results?: CriterionResult[];
  rejection_reason?: string;
  eval_duration_ms?: number;
  quote?: { price: number; change_percent: number; volume: number; gap_percent: number };
  indicators?: Record<string, number | null>;
  // signal
  side?: string;
  signal_price?: number;
  account_equity?: number;
  position_size_pct?: number;
  calculated_shares?: number;
  calculated_value?: number;
  stop_loss_pct?: number;
  stop_loss_price?: number;
  take_profit_pct?: number;
  take_profit_price?: number;
  // rejection
  rejection_details?: string;
  // execution
  success?: boolean;
  order_id?: string;
  broker?: string;
  filled_price?: number;
  requested_price?: number;
  slippage?: number;
  slippage_pct?: number;
  latency_ms?: number;
  fill_confirmed?: boolean;
  fill_confirmation_latency_ms?: number;
  error?: string;
  // exit
  exit_reason?: string;
  exit_price?: number;
  entry_price?: number;
  realized_pnl?: number;
  realized_pnl_pct?: number;
  hold_duration_seconds?: number;
  // scan_loop
  total_universe_size?: number;
  filtered_size?: number;
  patterns_scanned?: number;
  total_matches?: number;
  signals_generated?: number;
  scan_duration_ms?: number;
  circuit_breaker_status?: string;
  eodhd_credits?: number;
  trading_session?: string;
}

interface TriggerBlockerSummary {
  minutes: number;
  samples: number;
  matches_total: number;
  signals_total: number;
  patterns_engaged_total: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  signal_rate: number;
  recommended_action: string;
  top_blockers: Array<{ reason: string; count: number }>;
  eligibility_stages_avg?: Record<string, number>;
  eligibility_sequential_drop_avg?: Record<string, number>;
  eligibility_retain_pct_avg?: Record<string, number>;
  eligibility_funnel_samples?: number;
  dominant_eligibility_drop?: { gate: string; drop_pct: number } | null;
  eligibility_funnel_hint?: string | null;
}

const ELIGIBILITY_DROP_LABELS: Record<string, string> = {
  drop_trading_window: 'Trading window',
  drop_intraday_regime: 'Intraday regime',
  drop_macro_regime: 'Macro regime',
  drop_risk_limits: 'Risk limits',
  drop_target_universe: 'Target / universe',
  drop_to_evaluation: 'Evaluation',
};

// ============================================================
// Helpers
// ============================================================

const formatTime = (ts: string) =>
  new Date(ts).toLocaleTimeString('en-US', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZone: 'America/New_York',
  });

const formatDate = (ts: string) =>
  new Date(ts).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZone: 'America/New_York',
  });

const fmtNum = (v: number | null | undefined, decimals = 2) =>
  v != null ? v.toFixed(decimals) : '\u2014';

const fmtPct = (v: number | null | undefined) =>
  v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` : '\u2014';

const EVENT_BADGE_STYLES: Record<string, string> = {
  signal: 'bg-primary/10 text-primary border-primary/20',
  execution: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  rejection: 'bg-rose-500/10 text-rose-500 border-rose-500/20',
  scan_result: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  scan_loop: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  pattern_eval: 'bg-secondary text-secondary-foreground border-border',
  circuit_breaker: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  exit: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  system: 'bg-secondary text-secondary-foreground border-border',
};

const EVENT_ICONS: Record<string, React.ReactNode> = {
  signal: <Activity className="h-4 w-4" />,
  execution: <PlayCircle className="h-4 w-4" />,
  rejection: <XCircle className="h-4 w-4" />,
  scan_result: <Maximize className="h-4 w-4" />,
  scan_loop: <Maximize className="h-4 w-4" />,
  pattern_eval: <BrainCircuit className="h-4 w-4" />,
  circuit_breaker: <AlertOctagon className="h-4 w-4" />,
  exit: <LogOut className="h-4 w-4" />,
  system: <Cpu className="h-4 w-4" />,
};

const ASSET_COLORS: Record<string, string> = {
  stocks: 'bg-cyan-500',
  forex: 'bg-yellow-500',
  futures: 'bg-orange-500',
  crypto: 'bg-emerald-500',
};

const OPERATOR_LABELS: Record<string, string> = {
  gt: '>', gte: '\u2265', lt: '<', lte: '\u2264', eq: '=',
  between: 'between', neq: '\u2260',
};

// ============================================================
// Criterion result row
// ============================================================

const CriterionRow: React.FC<{ c: CriterionResult }> = ({ c }) => {
  const op = OPERATOR_LABELS[c.operator] || c.operator;
  const expected = c.expected_value2 != null
    ? `${fmtNum(c.expected_value)} \u2013 ${fmtNum(c.expected_value2)}`
    : fmtNum(c.expected_value);

  return (
    <tr className={`text-xs ${c.is_missing ? 'bg-amber-500/5' : c.passed ? 'bg-emerald-500/5' : 'bg-rose-500/5'}`}>
      <td className="py-1.5 px-2 font-medium">{c.field}</td>
      <td className="py-1.5 px-2 font-mono text-muted-foreground">{op} {expected}</td>
      <td className="py-1.5 px-2 font-mono">
        {c.is_missing
          ? <HelpCircle className="h-3.5 w-3.5 text-amber-500 inline" />
          : fmtNum(c.actual_value)}
      </td>
      <td className="py-1.5 px-2 text-center">
        {c.is_missing
          ? <HelpCircle className="h-3.5 w-3.5 text-amber-500 inline" />
          : c.passed
            ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 inline" />
            : <XCircle className="h-3.5 w-3.5 text-rose-500 inline" />}
      </td>
    </tr>
  );
};

// ============================================================
// Detailed log expanded content
// ============================================================

const DetailExpanded: React.FC<{ log: DetailedLog }> = ({ log }) => {
  switch (log.event_type) {

    case 'pattern_eval':
      return (
        <div className="space-y-3">
          {log.quote && (
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="outline" className="text-[10px]">Price ${fmtNum(log.quote.price)}</Badge>
              <Badge variant="outline" className="text-[10px]">Change {fmtPct(log.quote.change_percent)}</Badge>
              {log.quote.volume != null && (
                <Badge variant="outline" className="text-[10px]">
                  Vol {fmtNum(log.quote.volume / 1000, 0)}K
                </Badge>
              )}
              {log.quote.gap_percent != null && Math.abs(log.quote.gap_percent) > 0.5 && (
                <Badge variant="outline" className="text-[10px]">Gap {fmtPct(log.quote.gap_percent)}</Badge>
              )}
            </div>
          )}
          {log.indicators && Object.keys(log.indicators).length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(log.indicators)
                .filter(([, v]) => v != null)
                .slice(0, 8)
                .map(([k, v]) => (
                  <Badge key={k} variant="secondary" className="text-[10px]">{k}: {fmtNum(v as number)}</Badge>
                ))}
            </div>
          )}
          {log.criteria_results && log.criteria_results.length > 0 && (
            <div className="overflow-auto rounded border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="py-1.5 px-2 text-left text-xs font-medium text-muted-foreground">Field</th>
                    <th className="py-1.5 px-2 text-left text-xs font-medium text-muted-foreground">Required</th>
                    <th className="py-1.5 px-2 text-left text-xs font-medium text-muted-foreground">Actual</th>
                    <th className="py-1.5 px-2 text-center text-xs font-medium text-muted-foreground">Pass</th>
                  </tr>
                </thead>
                <tbody>
                  {log.criteria_results.map((c, i) => (
                    <CriterionRow key={i} c={c} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {log.eval_duration_ms != null && (
            <p className="text-[10px] text-muted-foreground">
              Eval took {log.eval_duration_ms.toFixed(1)} ms
            </p>
          )}
        </div>
      );

    case 'rejection':
      return (
        <div className="py-2">
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium">{log.rejection_reason}</p>
              {log.rejection_details && (
                <p className="text-xs mt-1 opacity-80">{log.rejection_details}</p>
              )}
            </AlertDescription>
          </Alert>
        </div>
      );

    case 'signal':
      return (
        <div className="space-y-2 py-2">
          <div className="flex flex-wrap gap-1.5">
            <Badge className="bg-primary/10 text-primary border-primary/20">{log.side?.toUpperCase()}</Badge>
            <Badge variant="outline" className="text-[10px]">Entry ${fmtNum(log.signal_price)}</Badge>
            <Badge variant="outline" className="text-[10px] bg-rose-500/10 text-rose-500 border-rose-500/20">
              SL ${fmtNum(log.stop_loss_price)} ({fmtPct(-(log.stop_loss_pct ?? 0))})
            </Badge>
            <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
              TP ${fmtNum(log.take_profit_price)} (+{fmtNum(log.take_profit_pct)}%)
            </Badge>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="secondary" className="text-[10px]">{log.calculated_shares ?? '?'} shares</Badge>
            <Badge variant="secondary" className="text-[10px]">${fmtNum(log.calculated_value, 0)} position</Badge>
            <Badge variant="secondary" className="text-[10px]">{fmtNum(log.position_size_pct)}% of equity</Badge>
            {log.account_equity && (
              <Badge variant="secondary" className="text-[10px]">Account ${(log.account_equity / 1000).toFixed(1)}K</Badge>
            )}
          </div>
        </div>
      );

    case 'execution':
      return (
        <div className="space-y-2 py-2">
          <div className="flex flex-wrap gap-1.5">
            {log.success
              ? <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">FILLED</Badge>
              : <Badge className="bg-rose-500/10 text-rose-500 border-rose-500/20">FAILED</Badge>}
            {log.order_id && (
              <Badge variant="outline" className="text-[10px]">Order {log.order_id.slice(0, 8)}...</Badge>
            )}
            {log.broker && <Badge variant="outline" className="text-[10px]">{log.broker.toUpperCase()}</Badge>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {log.requested_price != null && (
              <Badge variant="secondary" className="text-[10px]">Req ${fmtNum(log.requested_price)}</Badge>
            )}
            {log.filled_price != null && (
              <Badge variant="secondary" className="text-[10px]">Fill ${fmtNum(log.filled_price)}</Badge>
            )}
            {log.slippage_pct != null && (
              <Badge variant="outline" className={`text-[10px] ${(log.slippage_pct ?? 0) > 0 ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'}`}>
                Slippage {fmtPct(log.slippage_pct)}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {log.latency_ms != null && (
              <Badge variant="secondary" className="text-[10px]">Latency {log.latency_ms.toFixed(0)} ms</Badge>
            )}
            {log.fill_confirmed != null && (
              <Badge variant="outline" className={`text-[10px] ${log.fill_confirmed ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-amber-500/10 text-amber-500 border-amber-500/20'}`}>
                {log.fill_confirmed ? 'Fill confirmed' : 'Fill unconfirmed'}
              </Badge>
            )}
            {log.fill_confirmation_latency_ms != null && (
              <Badge variant="secondary" className="text-[10px]">
                Confirm {(log.fill_confirmation_latency_ms / 1000).toFixed(1)}s
              </Badge>
            )}
          </div>
          {log.error && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription>{log.error}</AlertDescription>
            </Alert>
          )}
        </div>
      );

    case 'exit':
      return (
        <div className="space-y-2 py-2">
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="outline" className={`${(log.realized_pnl_pct ?? 0) >= 0 ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-rose-500/10 text-rose-500 border-rose-500/20'}`}>
              PnL {fmtPct(log.realized_pnl_pct)}
            </Badge>
            <Badge variant="outline" className={`${(log.realized_pnl ?? 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
              ${fmtNum(log.realized_pnl)}
            </Badge>
            <Badge variant="secondary">
              {log.exit_reason?.replace(/_/g, ' ').toUpperCase() ?? '\u2014'}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {log.entry_price != null && (
              <Badge variant="secondary" className="text-[10px]">Entry ${fmtNum(log.entry_price)}</Badge>
            )}
            {log.exit_price != null && (
              <Badge variant="secondary" className="text-[10px]">Exit ${fmtNum(log.exit_price)}</Badge>
            )}
            {log.hold_duration_seconds != null && (
              <Badge variant="outline" className="text-[10px]">
                Held {log.hold_duration_seconds < 3600
                  ? `${(log.hold_duration_seconds / 60).toFixed(0)}m`
                  : `${(log.hold_duration_seconds / 3600).toFixed(1)}h`}
              </Badge>
            )}
          </div>
        </div>
      );

    case 'scan_loop':
      return (
        <div className="space-y-2 py-2">
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="secondary" className="text-[10px]">Universe {log.total_universe_size ?? 0}</Badge>
            <Badge variant="secondary" className="text-[10px]">Filtered {log.filtered_size ?? 0}</Badge>
            <Badge variant="secondary" className="text-[10px]">Patterns {log.patterns_scanned ?? 0}</Badge>
            <Badge variant="secondary" className="text-[10px]">Matches {log.total_matches ?? 0}</Badge>
            <Badge variant="secondary" className="text-[10px]">Signals {log.signals_generated ?? 0}</Badge>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {log.scan_duration_ms != null && (
              <Badge variant="outline" className="text-[10px]">{log.scan_duration_ms.toFixed(0)} ms</Badge>
            )}
            {log.trading_session && (
              <Badge
                variant="outline"
                className={
                  log.trading_session.startsWith('stale_gate')
                    ? 'text-[10px] border-amber-500/50 text-amber-600 bg-amber-500/10'
                    : 'text-[10px]'
                }
              >
                {log.trading_session.replace(/_/g, ' ')}
              </Badge>
            )}
            {log.circuit_breaker_status && log.circuit_breaker_status !== 'normal' && (
              <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-500 border-amber-500/20">
                CB: {log.circuit_breaker_status}
              </Badge>
            )}
            {log.eodhd_credits != null && log.eodhd_credits > 0 && (
              <Badge variant="outline" className="text-[10px]">{log.eodhd_credits} credits</Badge>
            )}
          </div>
        </div>
      );

    default:
      return null;
  }
};

// ============================================================
// Single log row
// ============================================================

const LogRow: React.FC<{ log: DetailedLog; isLive?: boolean }> = ({ log, isLive }) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = ['pattern_eval', 'rejection', 'signal', 'execution', 'exit', 'scan_loop']
    .includes(log.event_type);

  const badgeStyle = EVENT_BADGE_STYLES[log.event_type] ?? 'bg-secondary text-secondary-foreground border-border';
  const icon = EVENT_ICONS[log.event_type] ?? <Rss className="h-4 w-4" />;

  const primaryLabel = log.event_type === 'pattern_eval'
    ? (log.matched ? '\u2713 Match' : '\u2717 No match')
    : log.event_type.replace(/_/g, ' ');

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div
        className={`flex items-start gap-3 p-3 ${hasDetail ? 'cursor-pointer hover:bg-muted/50' : ''}`}
        onClick={() => hasDetail && setExpanded(e => !e)}
      >
        {/* Icon */}
        <div className="mt-0.5 text-muted-foreground">{icon}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className={`${badgeStyle} text-[10px]`}>
              {primaryLabel}
            </Badge>
            {log.symbol && <ClickableTicker symbol={log.symbol} />}
            {log.pattern_name && (
              <Badge variant="secondary" className="text-[10px]">
                {log.pattern_name}
              </Badge>
            )}
            {log.event_type === 'pattern_eval' && log.criteria_passed != null && (
              <span className="text-xs text-muted-foreground">
                {log.criteria_passed}/{log.criteria_total} criteria
              </span>
            )}
            <span className="text-[10px] text-muted-foreground ml-auto shrink-0">
              {isLive ? formatTime(log.timestamp) : formatDate(log.timestamp)}
            </span>
            {hasDetail && (
              <span className="text-muted-foreground">
                {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </span>
            )}
          </div>

          {/* Summary line */}
          {log.event_type !== 'scan_loop' && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {(log as any).details ||
                (log.event_type === 'rejection' ? (log.rejection_reason ?? '') : '') ||
                (log.event_type === 'exit'
                  ? `${fmtPct(log.realized_pnl_pct)} | ${log.exit_reason?.replace(/_/g, ' ')}`
                  : '') ||
                (log.event_type === 'execution' ? (log.success ? 'Filled' : `Failed: ${log.error ?? ''}`) : '') ||
                (log.event_type === 'signal'
                  ? `${log.side?.toUpperCase()} ${log.calculated_shares} shares @ $${fmtNum(log.signal_price)}`
                  : '')
              }
            </p>
          )}

          {/* Scan loop summary inline */}
          {log.event_type === 'scan_loop' && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {log.trading_session?.startsWith('stale_gate') ? (
                <span className="text-amber-600">Data stale — scan skipped (heartbeat only)</span>
              ) : (
                <>
                  {log.filtered_size ?? 0} candidates &middot; {log.total_matches ?? 0} matches &middot;{' '}
                  {log.signals_generated ?? 0} signals
                  {log.scan_duration_ms != null ? ` \u00B7 ${log.scan_duration_ms.toFixed(0)} ms` : ''}
                </>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {hasDetail && expanded && (
        <div className="border-t bg-muted/20 px-3 py-2 pl-10">
          <DetailExpanded log={log} />
        </div>
      )}
    </div>
  );
};

// ============================================================
// Stats bar
// ============================================================

const StatsBar: React.FC<{ logs: DetailedLog[] }> = ({ logs }) => {
  const counts = logs.reduce((acc, l) => {
    acc[l.event_type] = (acc[l.event_type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const missingData = logs.filter(l =>
    l.event_type === 'pattern_eval' &&
    l.criteria_results?.some(c => c.is_missing)
  ).length;

  return (
    <div className="flex flex-wrap items-center gap-2 py-2">
      {counts.scan_loop > 0 && (
        <Badge variant="outline" className="gap-1 text-[10px]">
          <Maximize className="h-3 w-3" /> {counts.scan_loop} scans
        </Badge>
      )}
      {counts.pattern_eval > 0 && (
        <Badge variant="outline" className="gap-1 text-[10px]">
          <BrainCircuit className="h-3 w-3" /> {counts.pattern_eval} evals
        </Badge>
      )}
      {counts.signal > 0 && (
        <Badge variant="outline" className="gap-1 text-[10px] bg-primary/10 text-primary border-primary/20">
          <Activity className="h-3 w-3" /> {counts.signal} signals
        </Badge>
      )}
      {counts.rejection > 0 && (
        <Badge variant="outline" className="gap-1 text-[10px] bg-rose-500/10 text-rose-500 border-rose-500/20">
          <XCircle className="h-3 w-3" /> {counts.rejection} rejected
        </Badge>
      )}
      {counts.execution > 0 && (
        <Badge variant="outline" className="gap-1 text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
          <PlayCircle className="h-3 w-3" /> {counts.execution} executed
        </Badge>
      )}
      {counts.exit > 0 && (
        <Badge variant="outline" className="gap-1 text-[10px] bg-amber-500/10 text-amber-500 border-amber-500/20">
          <LogOut className="h-3 w-3" /> {counts.exit} exits
        </Badge>
      )}
      {missingData > 0 && (
        <Badge variant="outline" className="gap-1 text-[10px] bg-amber-500/10 text-amber-500 border-amber-500/20">
          <HelpCircle className="h-3 w-3" /> {missingData} missing data
        </Badge>
      )}
    </div>
  );
};

// ============================================================
// Main page
// ============================================================

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'All Events' },
  { value: 'scan_loop', label: 'Scan Loops' },
  { value: 'pattern_eval', label: 'Pattern Evals' },
  { value: 'signal', label: 'Signals' },
  { value: 'rejection', label: 'Rejections' },
  { value: 'execution', label: 'Executions' },
  { value: 'exit', label: 'Exits' },
];

const ALL_ASSET_TYPE_OPTIONS = [
  { value: '', label: 'All Assets' },
  { value: 'stocks', label: 'Stocks' },
  { value: 'forex', label: 'Forex' },
  { value: 'futures', label: 'Futures' },
  { value: 'crypto', label: 'Crypto' },
];

const STOCKS_ASSET_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'stocks', label: 'Stocks' },
];

type DecisionLogProps = {
  /** When embedded under `/trade/:verticalId/decisions`, scope the asset filter. */
  initialAssetVertical?: string;
};

const DecisionLog: React.FC<DecisionLogProps> = ({ initialAssetVertical }) => {
  const { getToken } = useAuth();
  const { patterns, fetchPatterns } = useTitanStore();
  const { equities_only: equitiesOnly } = useEquitiesMode();
  const assetTypeOptions = equitiesOnly ? STOCKS_ASSET_TYPE_OPTIONS : ALL_ASSET_TYPE_OPTIONS;
  const scopedPatterns = filterStockPatterns(patterns, equitiesOnly);
  const [searchParams] = useSearchParams();

  // Filters
  const [patternId, setPatternId] = useState('');
  const [assetType, setAssetType] = useState('');
  const [eventType, setEventType] = useState('');
  const [showMatchedOnly, setShowMatchedOnly] = useState(false);
  const [viewMode, setViewMode] = useState<'live' | 'history' | 'trades'>('live');

  // Data
  const [liveDecisions, setLiveDecisions] = useState<DetailedLog[]>([]);
  const [historyLogs, setHistoryLogs] = useState<DetailedLog[]>([]);
  const [tradeLifecycles, setTradeLifecycles] = useState<any[]>([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [liveLoading, setLiveLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [triggerBlockers, setTriggerBlockers] = useState<TriggerBlockerSummary | null>(null);
  const [, setNewCount] = useState(0);
  const lastIdRef = useRef<string | null>(null);

  const PAGE_LIMIT = 100;

  // Load patterns for bot filter
  useEffect(() => { fetchPatterns(); }, [fetchPatterns]);

  const verticalQuery = searchParams.get('vertical');
  useEffect(() => {
    const v = initialAssetVertical || verticalQuery || '';
    if (v && assetTypeOptions.some((o) => o.value === v)) setAssetType(v);
    if (equitiesOnly && v && v !== 'stocks') setAssetType('stocks');
  }, [initialAssetVertical, verticalQuery, equitiesOnly, assetTypeOptions]);

  // ---- Live feed (in-memory, 2s poll) ----
  const fetchLive = useCallback(async () => {
    try {
      const data = await decisionsApi.list({ limit: 200 });
      let decisions: DetailedLog[] = (data.decisions || []).map((d: any) => ({
        ...d,
        ...(d.signal ? {
          side: d.signal.side,
          signal_price: d.signal.price,
          criteria_passed: d.signal.criteria_matched,
          criteria_total: d.signal.criteria_total,
        } : {}),
      }));

      if (equitiesOnly) {
        const stockPatternNames = new Set(scopedPatterns.map((p) => p.name));
        decisions = decisions.filter((d: any) => {
          const v = String(d.vertical || '').toLowerCase();
          if (v && v !== 'stocks') return false;
          if (d.pattern_name && !stockPatternNames.has(String(d.pattern_name))) {
            const p = scopedPatterns.find((x) => x.name === d.pattern_name);
            if (!p) return false;
          }
          return true;
        });
      }

      // Client-side filter by asset type (pattern + explicit vertical; do not treat
      // missing pattern_name as "matches all" — that pulled crypto broker errors into Stocks.)
      if (assetType) {
        const patternsByType = new Set(
          scopedPatterns.filter(p => p.asset_class === assetType).map(p => p.name)
        );
        decisions = decisions.filter((d: any) => {
          const v = String(d.vertical || '').toLowerCase();
          if (v === assetType) return true;
          if (d.pattern_name && patternsByType.has(String(d.pattern_name))) return true;
          return false;
        });
      }

      if (patternId) {
        const pname = patterns.find(p => p.id === patternId)?.name;
        if (pname) decisions = decisions.filter(d => d.pattern_name === pname);
      }

      if (eventType) decisions = decisions.filter(d => d.event_type === eventType);

      // Badge new items
      if (lastIdRef.current && decisions.length > 0) {
        const idx = decisions.findIndex(d => d.id === lastIdRef.current);
        if (idx > 0) setNewCount(n => n + idx);
      }
      if (decisions.length > 0) lastIdRef.current = decisions[0].id;

      setLiveDecisions(decisions);
    } catch (e) {
      console.error('Live fetch:', e);
    } finally {
      setLiveLoading(false);
    }
  }, [assetType, patternId, eventType, patterns]);

  // ---- Historical logs (Firestore) ----
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      if (eventType === 'pattern_eval' || (!eventType && showMatchedOnly)) {
        const data = await decisionsApi.evaluations({ 
          limit: PAGE_LIMIT, 
          ...(patternId ? { pattern_id: patternId } : {}),
          ...(showMatchedOnly ? { matched_only: true } : {})
        });
        setHistoryLogs((data.evaluations || []).map((e: any) => ({ ...e, event_type: 'pattern_eval' })));
      } else {
        const data = await decisionsApi.logs({ 
          limit: PAGE_LIMIT,
          ...(eventType ? { event_type: eventType } : {}),
          ...(patternId ? { pattern_id: patternId } : {}),
          ...(assetType ? { vertical: assetType } : {}),
        });
        const logs: DetailedLog[] = (data.logs || []).map((l: any) => ({ ...l, event_type: l.category || 'system' }));
        setHistoryLogs(logs);
      }
    } catch (e) {
      console.error('History fetch:', e);
    } finally {
      setLoading(false);
    }
  }, [eventType, showMatchedOnly, patternId, assetType, patterns]);

  const fetchTriggerBlockers = useCallback(async () => {
    try {
      const resp = await diagnosticsApi.triggerBlockers(60);
      setTriggerBlockers((resp?.data || null) as TriggerBlockerSummary | null);
    } catch (err) {
      console.error('Trigger blocker diagnostics fetch:', err);
    }
  }, []);

  // ---- Trade lifecycles (Firestore, on-demand) ----
  const fetchTrades = useCallback(async () => {
    setTradesLoading(true);
    try {
      const data = await decisionsApi.trades({ days: 1, limit: 100 });
      setTradeLifecycles(data.trades || []);
    } catch (e) {
      console.error('Failed to fetch trades', e);
    } finally {
      setTradesLoading(false);
    }
  }, []);

  // Initial loads
  useEffect(() => {
    setLiveLoading(true);
    fetchLive();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (viewMode === 'history') fetchHistory();
    if (viewMode === 'trades') fetchTrades();
  }, [viewMode, fetchHistory, fetchTrades]);

  // Auto-refresh live view
  useEffect(() => {
    if (viewMode !== 'live' || !autoRefresh) return;
    const id = setInterval(fetchLive, 8000);
    return () => clearInterval(id);
  }, [viewMode, autoRefresh, fetchLive]);

  useEffect(() => {
    if (viewMode !== 'live') return;
    fetchTriggerBlockers();
    const id = setInterval(fetchTriggerBlockers, 30000);
    return () => clearInterval(id);
  }, [viewMode, fetchTriggerBlockers]);

  // Re-fetch when filters change
  useEffect(() => {
    if (viewMode === 'live') {
      fetchLive();
    } else if (viewMode === 'trades') {
      fetchTrades();
    } else {
      fetchHistory();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patternId, assetType, eventType, showMatchedOnly]);

  const handleRefresh = () => {
    setNewCount(0);
    if (viewMode === 'live') fetchLive();
    else if (viewMode === 'trades') fetchTrades();
    else fetchHistory();
  };

  const displayLogs = viewMode === 'live' ? liveDecisions : historyLogs;
  const isLoading = viewMode === 'live' ? liveLoading : viewMode === 'trades' ? tradesLoading : loading;

  // Build pattern options grouped by asset class
  const patternOptions: { value: string; label: string; assetClass?: string }[] = [
    { value: '', label: 'All Bots' },
    ...scopedPatterns
      .sort((a, b) => (a.asset_class ?? '').localeCompare(b.asset_class ?? '') || a.name.localeCompare(b.name))
      .map(p => ({
        value: p.id,
        label: `${p.name}`,
        assetClass: p.asset_class,
      })),
  ];

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-xl sm:text-2xl font-bold text-foreground">Decision Log</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Complete audit trail -- scan results, criteria evaluations, signals, rejections, and exits
            </p>
          </div>
          <Link
            to="/decisions/bots"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline shrink-0"
          >
            <BrainCircuit className="h-4 w-4" />
            Per-bot insight
          </Link>
        </div>

        <DataAvailabilityPanel />

        <div className="space-y-3">
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Live / History toggle */}
            <div className="inline-flex items-center rounded-md bg-muted p-1">
              {([
                { value: 'live', icon: <Radio className="h-3.5 w-3.5" />, label: 'Live' },
                { value: 'trades', icon: <Activity className="h-3.5 w-3.5" />, label: 'Trades' },
                { value: 'history', icon: <Rss className="h-3.5 w-3.5" />, label: 'History' },
              ] as const).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setViewMode(opt.value)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${
                    viewMode === opt.value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {opt.icon}
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Bot filter */}
            <div className="w-full sm:w-48">
              <Select value={patternId || '_all'} onValueChange={(v) => setPatternId(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="All Bots" />
                </SelectTrigger>
                <SelectContent>
                  {patternOptions.map(o => (
                    <SelectItem key={o.value} value={o.value || '_all'}>
                      <div className="flex items-center gap-1.5">
                        {o.assetClass && (
                          <div className={`h-2 w-2 rounded-full ${ASSET_COLORS[o.assetClass] || 'bg-muted-foreground'}`} />
                        )}
                        <span>{o.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Asset type filter */}
            <div className="w-full sm:w-36">
              <Select value={assetType || '_all'} onValueChange={(v) => setAssetType(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="All Assets" />
                </SelectTrigger>
                <SelectContent>
                  {assetTypeOptions.map(o => (
                    <SelectItem key={o.value} value={o.value || '_all'}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Event type filter */}
            <div className="w-full sm:w-40">
              <Select value={eventType || '_all'} onValueChange={(v) => setEventType(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="All Events" />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPE_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value || '_all'}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Matched only toggle (for pattern_eval) */}
            {(eventType === 'pattern_eval' || eventType === '') && viewMode === 'history' && (
              <Button
                variant={showMatchedOnly ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowMatchedOnly(m => !m)}
                className="gap-1.5"
              >
                <Filter className="h-3.5 w-3.5" />
                Matched only
              </Button>
            )}

            <div className="flex-1" />

            {/* Auto-refresh (live only) */}
            {viewMode === 'live' && (
              <Badge
                variant="outline"
                className={`cursor-pointer gap-1.5 ${autoRefresh ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'text-muted-foreground'}`}
                onClick={() => { setAutoRefresh(r => !r); setNewCount(0); }}
              >
                <Radio className="h-3 w-3" />
                {autoRefresh ? 'Live' : 'Paused'}
              </Badge>
            )}

            <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={isLoading}>
              {isLoading
                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>

          {/* Stats bar */}
          {viewMode !== 'trades' && displayLogs.length > 0 && <StatsBar logs={displayLogs} />}

          {/* Trigger-blocker summary (live diagnostics) */}
          {viewMode === 'live' && triggerBlockers && (
            <div className="rounded-lg border bg-card p-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="text-[10px]">
                  {triggerBlockers.samples} scans ({triggerBlockers.minutes}m)
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  engaged {triggerBlockers.patterns_engaged_total}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  matches {triggerBlockers.matches_total}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  signals {triggerBlockers.signals_total}
                </Badge>
                <Badge
                  variant="outline"
                  className={`text-[10px] ${
                    (triggerBlockers.severity === 'critical' || triggerBlockers.severity === 'high')
                      ? 'bg-rose-500/10 text-rose-500 border-rose-500/20'
                      : triggerBlockers.severity === 'medium'
                        ? 'bg-amber-500/10 text-amber-500 border-amber-500/20'
                        : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                  }`}
                >
                  severity {triggerBlockers.severity}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  signal rate {fmtNum(
                    triggerBlockers.signal_rate != null ? triggerBlockers.signal_rate * 100 : null,
                    1,
                  )}%
                </Badge>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Recommended action: {triggerBlockers.recommended_action}
              </p>
              {triggerBlockers.eligibility_funnel_samples != null &&
                triggerBlockers.eligibility_funnel_samples > 0 &&
                triggerBlockers.eligibility_stages_avg && (
                  <div className="mt-2 space-y-1.5 rounded-md border border-border/60 bg-muted/30 p-2">
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                      Eligibility funnel (avg / {triggerBlockers.eligibility_funnel_samples} scans)
                    </p>
                    <p className="text-xs text-foreground font-mono">
                      stock {triggerBlockers.eligibility_stages_avg.stock_candidates ?? '—'} → tw{' '}
                      {triggerBlockers.eligibility_stages_avg.after_trading_window ?? '—'} → eval{' '}
                      {triggerBlockers.eligibility_stages_avg.evaluated ?? '—'}
                      {triggerBlockers.eligibility_retain_pct_avg?.evaluated != null && (
                        <span className="text-muted-foreground font-sans">
                          {' '}
                          ({fmtNum(triggerBlockers.eligibility_retain_pct_avg.evaluated, 0)}% retain vs stock)
                        </span>
                      )}
                    </p>
                    {triggerBlockers.dominant_eligibility_drop && (
                      <Badge variant="outline" className="text-[10px]">
                        Largest stage drop:{' '}
                        {ELIGIBILITY_DROP_LABELS[triggerBlockers.dominant_eligibility_drop.gate] ||
                          triggerBlockers.dominant_eligibility_drop.gate}{' '}
                        ({triggerBlockers.dominant_eligibility_drop.drop_pct}%)
                      </Badge>
                    )}
                  </div>
                )}
              <div className="mt-2 flex flex-wrap gap-2">
                {(triggerBlockers.top_blockers || []).slice(0, 5).map((item) => (
                  <Badge key={item.reason} variant="secondary" className="text-[10px]">
                    {item.reason} ({item.count})
                  </Badge>
                ))}
                {(!triggerBlockers.top_blockers || triggerBlockers.top_blockers.length === 0) && (
                  <span className="text-xs text-muted-foreground">
                    No dominant blocker in the recent window.
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Loading bar */}
          {isLoading && <Progress value={undefined} className="h-1" />}

          {/* ====== TRADES VIEW ====== */}
          {viewMode === 'trades' && (
            <>
              {tradesLoading && <Progress value={undefined} className="h-1" />}
              {!tradesLoading && tradeLifecycles.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16">
                  <Activity className="h-10 w-10 text-muted-foreground mb-3" />
                  <p className="text-lg font-medium">No completed trades</p>
                  <p className="text-sm text-muted-foreground">
                    Completed trades will appear here with full signal &rarr; execution &rarr; exit details.
                  </p>
                </div>
              )}
              {tradeLifecycles.length > 0 && (
                <div className="space-y-2">
                  {tradeLifecycles.map((trade: any, i: number) => {
                    const s = trade.summary || {};
                    const isWin = (s.pnl || 0) > 0;
                    return (
                      <div key={i} className="rounded-lg border bg-card p-3 space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className="font-semibold">{s.symbol || '?'}</Badge>
                          <Badge variant="outline" className={s.side === 'buy' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-rose-500/10 text-rose-500 border-rose-500/20'}>
                            {s.side?.toUpperCase() || '?'}
                          </Badge>
                          <span className="text-sm text-muted-foreground font-mono">
                            ${fmtNum(s.entry_price)} &rarr; ${fmtNum(s.exit_price)}
                          </span>
                          <span className={`text-sm font-semibold font-mono ${isWin ? 'text-emerald-500' : (s.pnl || 0) < 0 ? 'text-rose-500' : 'text-muted-foreground'}`}>
                            {isWin ? '+' : ''}{fmtNum(s.pnl)} ({isWin ? '+' : ''}{fmtNum(s.pnl_pct, 1)}%)
                          </span>
                          <Badge variant="secondary" className="text-[10px]">
                            {s.exit_reason?.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {s.hold_minutes != null ? `${fmtNum(s.hold_minutes, 0)}min` : '\u2014'}
                          </span>
                          {s.pattern_name && (
                            <Badge variant="outline" className="text-[10px]">{s.pattern_name}</Badge>
                          )}
                          <span className="text-[10px] text-muted-foreground ml-auto">
                            {s.exit_time ? new Date(s.exit_time).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                        {/* Execution details row */}
                        {trade.execution && (
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            <span>Slippage: {trade.execution.slippage_pct?.toFixed(3) ?? '\u2014'}%</span>
                            <span>Latency: {trade.execution.latency_ms?.toFixed(0) ?? '\u2014'}ms</span>
                            <span>Qty: {s.quantity}</span>
                            <span>Broker: {trade.execution.broker || '\u2014'}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="text-center py-2">
                    <p className="text-xs text-muted-foreground">
                      {tradeLifecycles.length} trades (today)
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ====== LIVE / HISTORY VIEWS ====== */}
          {viewMode !== 'trades' && (
            <>
              {/* Empty state */}
              {!isLoading && displayLogs.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16">
                  <Rss className="h-10 w-10 text-muted-foreground mb-3" />
                  <p className="text-lg font-medium">No decisions found</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {viewMode === 'live'
                      ? 'Start the controller -- decisions will appear here in real time.'
                      : 'No historical logs match the current filters. Try broadening your selection.'}
                  </p>
                </div>
              )}

              {/* Log list */}
              {displayLogs.length > 0 && (
                <div className="space-y-2">
                  {displayLogs.map(log => (
                    <LogRow key={log.id} log={log} isLive={viewMode === 'live'} />
                  ))}
                  {viewMode === 'history' && displayLogs.length >= PAGE_LIMIT && (
                    <div className="text-center py-3">
                      <p className="text-xs text-muted-foreground">
                        Showing latest {PAGE_LIMIT} entries -- narrow filters to see more specific results
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DecisionLog;
